import { useCallback, useEffect, useState } from 'react';
import type { NotificationListItem } from '@flymind/shared';
import { api } from '../lib/api-client';

const PAGE_SIZE = 10;

interface UseNotificationsReturn {
  notifications: NotificationListItem[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refetch: () => Promise<void>;
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<NotificationListItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const fetchInitial = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [notifData, countData] = await Promise.all([
        api.get<{ items: NotificationListItem[]; total: number; hasNext: boolean; unreadCount: number }>(`/notifications?page=1&pageSize=${PAGE_SIZE}`),
        api.get<{ unreadCount: number }>('/notifications/unread-count'),
      ]);
      const items = notifData.items ?? [];
      setNotifications(items);
      setUnreadCount(countData.unreadCount ?? notifData.unreadCount ?? 0);
      setPage(1);
      setHasMore(notifData.hasNext ?? false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nel caricamento delle notifiche');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  const loadMore = useCallback(async () => {
    if (!hasMore) return;
    try {
      const nextPage = page + 1;
      const moreData = await api.get<{ items: NotificationListItem[]; hasNext: boolean }>(
        `/notifications?page=${nextPage}&pageSize=${PAGE_SIZE}`,
      );
      const items = moreData.items ?? [];
      setNotifications((prev) => [...prev, ...items]);
      setPage(nextPage);
      setHasMore(moreData.hasNext ?? false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore nel caricamento');
    }
  }, [hasMore, page]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, letta: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, letta: true })));
      setUnreadCount(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore');
    }
  }, []);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    refetch: fetchInitial,
  };
}
